import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { db } from '../firebase/config';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';

const AuthorizationContext = createContext();

export function useAuthorization() {
  return useContext(AuthorizationContext);
}

const ROLES = {
  ADMIN: 'admin',
  MANAGER: 'manager',
  USER: 'user'
};

const PERMISSIONS = {
  CREATE_TEMPLATE: ['admin', 'manager'],
  EDIT_TEMPLATE: ['admin', 'manager'],
  DELETE_TEMPLATE: ['admin'],
  VIEW_ALL_LOCATIONS: ['admin'],
  MANAGE_USERS: ['admin'],
  VIEW_REPORTS: ['admin', 'manager'],
  SUBMIT_FORMS: ['admin', 'manager', 'user'],
  VIEW_ASSIGNED_FORMS: ['admin', 'manager', 'user'],
  VIEW_ABOUT: ['admin', 'manager'],
  VIEW_FORMS: ['admin', 'manager'],
  VIEW_ACTIVE_FORMS: ['admin', 'manager', 'user'],
  VIEW_WASTE: ['admin', 'manager', 'user'],
  VIEW_DASHBOARD: ['admin', 'manager'],
  MANAGE_CATEGORIES: ['admin'],
  MANAGE_DEPARTMENTS: ['admin'],
  MANAGE_TYPES: ['admin']
};

export function AuthorizationProvider({ children }) {
  const { currentUser } = useAuth();
  const [userRole, setUserRole] = useState(null);
  const [assignedLocations, setAssignedLocations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchUserData() {
      console.log('AuthorizationProvider: Fetching user data...', { 
        currentUser: currentUser ? {
          uid: currentUser.uid,
          email: currentUser.email
        } : null 
      });
      
      if (!currentUser?.uid) {
        console.log('No current user, clearing authorization state');
        setUserRole(null);
        setAssignedLocations([]);
        setLoading(false);
        return;
      }

      try {
        const userDocRef = doc(db, 'users', currentUser.uid);
        console.log('Fetching user document:', userDocRef.path);
        
        const userDoc = await getDoc(userDocRef);
        console.log('User document exists:', userDoc.exists());
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          console.log('User data from Firestore:', {
            ...userData,
            uid: currentUser.uid,
            email: currentUser.email
          });
          
          // Set role, defaulting to 'user' if not specified
          const role = userData.role?.toLowerCase() || 'user';
          console.log('Setting user role to:', role);
          setUserRole(role);
          
          // Set assigned locations
          const locations = userData.assignedLocations || [];
          console.log('Setting assigned locations:', locations);
          setAssignedLocations(locations);

          // Update user document if role is missing or not lowercase
          if (!userData.role || userData.role !== role) {
            console.log('Updating user document with normalized role');
            await updateDoc(userDocRef, {
              role: role
            });
          }
        } else {
          console.warn('User document does not exist in Firestore, creating with user role');
          const newUserData = {
            email: currentUser.email,
            role: 'user',
            assignedLocations: []
          };
          await setDoc(userDocRef, newUserData);
          setUserRole('user');
          setAssignedLocations([]);
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
        setUserRole('user');
        setAssignedLocations([]);
      } finally {
        setLoading(false);
      }
    }

    fetchUserData();
  }, [currentUser]);

  const hasPermission = (permission) => {
    if (!permission || !userRole) {
      console.log('Permission check failed: no permission or userRole', { permission, userRole });
      return false;
    }
    
    const normalizedUserRole = userRole.toLowerCase();
    
    console.log('Checking permission:', {
      permission,
      userRole: normalizedUserRole,
      isAdmin: normalizedUserRole === ROLES.ADMIN.toLowerCase(),
      allowed: PERMISSIONS[permission]?.some(role => role.toLowerCase() === normalizedUserRole) || false
    });

    // Admin users have all permissions
    if (normalizedUserRole === ROLES.ADMIN.toLowerCase()) {
      console.log('User is admin, granting permission');
      return true;
    }

    // For other roles, check the permission list
    const hasPermissionResult = PERMISSIONS[permission]?.some(role => role.toLowerCase() === normalizedUserRole) || false;
    console.log('Permission check result:', hasPermissionResult);
    return hasPermissionResult;
  };

  const canAccessLocation = (locationId) => {
    console.log('Checking location access:', {
      locationId,
      userRole,
      assignedLocations,
      isAdmin: userRole === ROLES.ADMIN
    });

    // Admin users can access all locations
    if (userRole === ROLES.ADMIN) {
      console.log('User is admin, granting location access');
      return true;
    }

    // For other roles, check assigned locations
    const hasLocationAccess = assignedLocations?.includes(locationId) || false;
    console.log('Location access check result:', hasLocationAccess);
    return hasLocationAccess;
  };

  const updateUserRole = async (userId, newRole) => {
    try {
      const userDocRef = doc(db, 'users', userId);
      await updateDoc(userDocRef, {
        role: newRole.toLowerCase(),
        updatedAt: new Date().toISOString()
      });
      console.log(`Updated user ${userId} role to ${newRole}`);
      return true;
    } catch (error) {
      console.error('Error updating user role:', error);
      return false;
    }
  };

  const value = {
    userRole,
    assignedLocations,
    hasPermission,
    canAccessLocation,
    updateUserRole,
    loading,
    ROLES,
    PERMISSIONS
  };

  console.log('Authorization context value:', value);

  return (
    <AuthorizationContext.Provider value={value}>
      {!loading && children}
    </AuthorizationContext.Provider>
  );
}
